/**
 * The sidebar: a white panel floating on the page (a full-height drawer on
 * phones) with the brand, tenant, the task-based Manage navigation
 * (src/layouts/navigation.js) and a card that switches to "Existing reports"
 * (the classic dashboard → tab list). The signed-in user sits in the top bar.
 * Only pages the user may open in this tenant are listed.
 */

import { useState } from "react";
import { NavLink } from "react-router-dom";
import { DASHBOARDS } from "../../shared/catalog.js";
import { NavIcon } from "./NavIcon.jsx";
import { containsPage, landingOf, tabOf, visibleNav } from "./navigation.js";
import { TenantSwitcher } from "../tenant/TenantSwitcher.jsx";
import { tabEnabled, tabTitle, useTenant } from "../tenant/TenantProvider.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";
import { Icon, IconButton } from "../components/ui/index.js";

const MODE_KEY = "content-analytics:nav";

function rememberedMode() {
  try {
    return localStorage.getItem(MODE_KEY) === "reports" ? "reports" : "manage";
  } catch {
    return "manage";
  }
}

const row = "relative flex w-full items-center gap-3 rounded-control px-3 text-left text-[14px] transition-colors duration-150 max-lg:min-h-11";
// The active entry: an accent tint, accent text, and a marker on the panel's edge.
const tone = (active) =>
  active
    ? "bg-side-active font-semibold text-side-active-ink before:absolute before:top-1/2 before:-left-3 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-r-full before:bg-accent"
    : "text-side-ink-2 hover:bg-side-hover hover:text-side-ink";
const groupLabel = "px-3 pb-2 text-[12px] font-medium text-side-ink-3";

/** One nav entry and, when open, its children. `depth` 0 = top level (with icon). */
function Entry({ entry, current, search, collapsed, depth = 0 }) {
  const { current: tenant } = useTenant();
  const label = entry.label ?? tabTitle(tabOf(entry.page), tenant);
  const here = containsPage(entry, current);
  const open = !collapsed && entry.children?.length > 0 && (entry.alwaysOpen || here);
  const to = landingOf(entry);
  const pad = depth === 0 ? "py-2.5" : "py-2";
  const indent = depth === 0 ? "" : depth === 1 ? "pl-[42px]" : "pl-[56px]";

  if (entry.soon) {
    return (
      <li>
        <span className={`${row} ${pad} ${collapsed ? "justify-center" : ""} cursor-not-allowed text-side-ink-3`} title={entry.soon} aria-disabled="true">
          <NavIcon name={entry.icon} />
          {!collapsed && (
            <>
              <span className="truncate">{entry.label}</span>
              <span className="ml-auto rounded-full border border-side-line px-1.5 text-[10px] tracking-wide uppercase">Soon</span>
            </>
          )}
        </span>
      </li>
    );
  }

  return (
    <li>
      <NavLink
        to={{ pathname: `/${to}`, search }}
        className={`${row} ${pad} ${indent} ${collapsed ? "justify-center" : ""} ${tone(entry.page === current)} ${!entry.page && here ? "text-side-ink" : ""}`}
        title={collapsed ? label : undefined}
        aria-current={entry.page === current ? "page" : undefined}
      >
        {depth === 0 && <NavIcon name={entry.icon} />}
        {!collapsed && <span className="min-w-0 truncate">{label}</span>}
        {/* Entries with a sub-menu: an arrow that points down while it is open. */}
        {!collapsed && entry.children?.length > 0 && (
          <Icon name="chevronDown" className={`ml-auto size-4 shrink-0 text-side-ink-3 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
        )}
      </NavLink>
      {open && (
        <ul className="mt-1 mb-1.5 flex animate-fade-in flex-col gap-0.5">
          {entry.children.map((c) => (
            <Entry key={c.key} entry={c} current={current} search={search} collapsed={collapsed} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** Each classic report's icon, by "dashboard/tab"; anything unlisted gets the bar chart. */
const REPORT_ICONS = {
  "acquisition/overview": "chart",
  "acquisition/web": "globe",
  "acquisition/app": "phone",
  "acquisition/funnel": "funnel",
  "engagement/overview": "grid",
  "engagement/video": "play",
  "engagement/reels": "play",
  "engagement/medwiki": "book",
  "engagement/webinar": "monitor",
  "engagement/lms": "cap",
  "engagement/surveys": "clipboard",
  "engagement/epub": "book",
  "engagement/channels": "broadcast",
  "engagement/behaviour": "activity",
  "engagement/speciality": "stethoscope",
  "monitoring/unusual": "alert",
};

/** The classic list: every dashboard and its tabs. */
function ExistingReports({ allowed, current, search, collapsed }) {
  const { current: tenant } = useTenant();
  const dashboards = DASHBOARDS.map((d) => ({ ...d, tabs: d.tabs.filter(allowed) })).filter((d) => d.tabs.length);
  return dashboards.map((d) => (
    <div key={d.id} className="mb-5">
      {!collapsed && <p className={groupLabel}>{d.title}</p>}
      <ul className="flex flex-col gap-1">
        {d.tabs.map((t) => {
          const page = `${d.id}/${t.id}`;
          return (
            <li key={t.id}>
              <NavLink
                to={{ pathname: `/${page}`, search }}
                className={`${row} py-2 ${collapsed ? "justify-center" : ""} ${tone(page === current)}`}
                title={`${d.title} · ${tabTitle(t, tenant)}`}
                aria-current={page === current ? "page" : undefined}
              >
                <NavIcon name={REPORT_ICONS[page] ?? "reports"} />
                {!collapsed && <span className="min-w-0 truncate">{tabTitle(t, tenant)}</span>}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </div>
  ));
}

/** `mobileOpen` / `onClose`: below desktop width the sidebar is a drawer over the page. */
export function Sidebar({ current, search, mobileOpen = false, onClose }) {
  const { current: tenant, tenants, allowAll } = useTenant();
  // A single-tenant login has nothing to switch to.
  const canSwitchTenant = allowAll || tenants.length > 1;
  const { can } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [chosenMode, setModeState] = useState(rememberedMode);
  // Reports-only tenants (Docflix) get the existing reports and no toggle.
  const mode = tenant.reportsOnly ? "reports" : chosenMode;
  const setMode = (m) => {
    setModeState(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      // storage blocked: the choice lasts for this visit
    }
  };
  const toggleMode = () => setMode(mode === "manage" ? "reports" : "manage");
  const allowed = (t) => tabEnabled(t, tenant) && can(t.permission);
  const nav = visibleNav(allowed);
  const narrow = collapsed && !mobileOpen;

  // lg:z-40 keeps the sidebar (and the tenant / user menus that open past its
  // edge) above the page; the page's own sticky bar is z-30.
  return (
    <>
      {mobileOpen && <button type="button" aria-label="Close navigation" tabIndex={-1} onClick={onClose} className="fixed inset-0 z-40 animate-fade-in bg-[#0b1220]/50 backdrop-blur-[2px] lg:hidden" />}
      <aside
        className={`no-print fixed inset-y-0 left-0 z-50 flex h-screen shrink-0 flex-col bg-side text-side-ink transition-[transform,width] duration-200 ease-out lg:sticky lg:top-3 lg:z-40 lg:my-3 lg:ml-3 lg:h-[calc(100vh-24px)] lg:translate-x-0 lg:rounded-card lg:shadow-card ${mobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}`}
        style={{ width: narrow ? 76 : 264 }}
        aria-label="Sidebar"
      >
        <div className={`flex items-center gap-2.5 pt-5 pb-4 ${narrow ? "flex-col px-2" : "px-4"}`}>
          <span
            aria-hidden
            className={`grid size-10 shrink-0 place-items-center overflow-hidden rounded-[12px] text-[15px] font-bold ${tenant.logo ? "bg-surface-1 ring-1 ring-line" : "bg-accent text-on-accent"}`}
          >
            {tenant.logo ? <img src={tenant.logo} alt="" className="size-7 object-contain" /> : tenant.name.slice(0, 1).toUpperCase()}
          </span>
          {!narrow && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-[17px] leading-tight font-bold tracking-[-0.01em] text-side-ink">{tenant.name}</p>
              <p className="mt-0.5 text-[12px] whitespace-nowrap text-side-ink-3">Content analytics</p>
            </div>
          )}
          {mobileOpen && <IconButton icon="close" label="Close navigation" tone="sidebar" size="lg" onClick={onClose} className="lg:hidden" />}
          <IconButton
            icon={collapsed ? "chevronRight" : "chevronLeft"}
            label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            tone="sidebar"
            size="sm"
            onClick={() => setCollapsed((c) => !c)}
            className="hidden lg:inline-flex"
          />
        </div>

        {!narrow && canSwitchTenant && (
          <div className="px-3 pb-3">
            <TenantSwitcher variant="sidebar" />
          </div>
        )}

        <nav className="scroll-thin flex-1 overflow-y-auto px-3 py-2" aria-label={mode === "manage" ? "Manage" : "Existing reports"}>
          {mode === "manage" ? (
            <>
              {!narrow && <p className={groupLabel}>Manage</p>}
              <ul className="flex flex-col gap-1">
                {nav.map((e) => (
                  <Entry key={e.key} entry={e} current={current} search={search} collapsed={narrow} />
                ))}
              </ul>
            </>
          ) : (
            <ExistingReports allowed={allowed} current={current} search={search} collapsed={narrow} />
          )}
        </nav>

        {/* Switches the list between the Manage navigation and every classic report. */}
        {!tenant.reportsOnly && (
        <div className="p-3">
          {narrow ? (
            <IconButton icon={mode === "manage" ? "reports" : "grid"} label={mode === "manage" ? "Existing reports" : "Back to Manage"} onClick={toggleMode} className="mx-auto flex" />
          ) : (
            <div className="rounded-card bg-surface-2 p-3.5">
              <p className="flex items-center gap-2 text-[13.5px] font-semibold text-side-ink">
                <NavIcon name={mode === "manage" ? "reports" : "grid"} className="text-accent" />
                {mode === "manage" ? "Existing reports" : "Manage view"}
              </p>
              <p className="mt-1 text-[12px] leading-snug text-side-ink-3">
                {mode === "manage" ? "Every report, grouped as before: Acquisition, Content engagement, Monitoring." : "Back to the task-based navigation."}
              </p>
              <button
                type="button"
                onClick={toggleMode}
                className="mt-3 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-control bg-accent text-[12.5px] font-medium text-on-accent transition-colors hover:bg-accent-hover"
              >
                {mode === "manage" ? "Open reports" : "Back to Manage"}
                <Icon name="arrowRight" className="size-3.5" />
              </button>
            </div>
          )}
        </div>
        )}
      </aside>
    </>
  );
}
